/*
 * Shipping Protection — cart toggle
 * ---------------------------------
 * Adds/removes the hidden `shipping-protection` variant as a cart line, then
 * refreshes whatever cart UI the theme uses. Theme-agnostic: it discovers the
 * cart sections present on the page rather than hard-coding section ids.
 *
 * Refresh strategy, in order:
 *   1. Sections returned inline by /cart/add.js | /cart/change.js
 *   2. A follow-up ?sections=<discovered cart sections> fetch
 *   3. Dawn-family fallback: re-render `cart-drawer` into `.drawer__inner`
 *   4. Last resort: full page reload (so the total is never stale/wrong)
 */
(function () {
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function post(path, body) {
    return fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error(path + ' -> ' + res.status);
      return res.json();
    });
  }

  /* Every cart-ish section currently rendered on the page. */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('[id^="shopify-section-"]').forEach(function (el) {
      var id = el.id.replace('shopify-section-', '');
      if (/cart/i.test(id) && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function swapSections(sections) {
    var swapped = 0;
    Object.keys(sections || {}).forEach(function (id) {
      var target = document.getElementById('shopify-section-' + id);
      if (!target || typeof sections[id] !== 'string') return;
      var fresh = new DOMParser()
        .parseFromString(sections[id], 'text/html')
        .getElementById('shopify-section-' + id);
      if (!fresh) return;
      target.innerHTML = fresh.innerHTML;
      swapped++;
    });
    return swapped;
  }

  function fetchSections(ids) {
    if (!ids.length) return Promise.resolve(0);
    return fetch(ROOT + '?sections=' + ids.join(','))
      .then(function (res) { return res.ok ? res.json() : {}; })
      .then(swapSections)
      .catch(function () { return 0; });
  }

  /* Dawn-family drawers rendered from layout (not wrapped in a section). */
  function refreshDawnDrawer() {
    var inner = document.querySelector('cart-drawer .drawer__inner');
    if (!inner) return Promise.resolve(0);
    return fetch(ROOT + '?sections=cart-drawer')
      .then(function (res) { return res.ok ? res.json() : {}; })
      .then(function (data) {
        if (!data['cart-drawer']) return 0;
        var fresh = new DOMParser()
          .parseFromString(data['cart-drawer'], 'text/html')
          .querySelector('.drawer__inner');
        if (!fresh) return 0;
        inner.innerHTML = fresh.innerHTML;
        return 1;
      })
      .catch(function () { return 0; });
  }

  function refresh(inlineSections, ids) {
    var done = swapSections(inlineSections);
    if (done) return Promise.resolve(done);
    return fetchSections(ids).then(function (n) {
      return n ? n : refreshDawnDrawer();
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-sp-toggle]');
    if (!toggle) return;

    var row = toggle.closest('[data-shipping-protection]');
    if (!row || row.dataset.busy === '1') return;

    var variantId = Number(row.dataset.variantId);
    if (!variantId) return;

    var wasOn = toggle.classList.contains('is-on');
    var ids = cartSectionIds();

    /* Optimistic flip — reverted in .catch if the cart rejects it. */
    row.dataset.busy = '1';
    row.classList.add('is-busy');
    toggle.classList.toggle('is-on', !wasOn);
    toggle.setAttribute('aria-checked', String(!wasOn));

    var request = wasOn
      ? post('cart/change.js', { id: String(variantId), quantity: 0, sections: ids })
      : post('cart/add.js', { items: [{ id: variantId, quantity: 1 }], sections: ids });

    request
      .then(function (data) { return refresh(data && data.sections, ids); })
      .then(function (updated) {
        if (!updated && !document.getElementById('cdc-drawer')) window.location.reload();
        document.dispatchEvent(
          new CustomEvent('shipping-protection:changed', { detail: { on: !wasOn } })
        );
      })
      .catch(function () {
        toggle.classList.toggle('is-on', wasOn);
        toggle.setAttribute('aria-checked', String(wasOn));
      })
      .finally(function () {
        row.dataset.busy = '';
        row.classList.remove('is-busy');
      });
  });

  /*
   * Guard: never let protection be the only thing in the cart.
   * If it is, drop it so the customer can't check out with just a fee.
   */
  document.addEventListener('shipping-protection:changed', function () {
    fetch(ROOT + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var row = document.querySelector('[data-shipping-protection]');
        if (!row || cart.items.length !== 1) return;
        if (String(cart.items[0].variant_id) !== row.dataset.variantId) return;
        return post('cart/change.js', { id: row.dataset.variantId, quantity: 0 })
          .then(function () { window.location.reload(); });
      })
      .catch(function () { /* non-critical */ });
  });
})();
